/**
 * AdminAdmissionLettersPage — Round 29
 * Lists programme-enrolled students, search by name/email, upload an
 * admission letter (file OR external URL like Google Drive), view, replace
 * or delete the current letter per student.
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { MagnifyingGlass, UploadSimple, Eye, Trash, FileText, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAdmissionLettersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [programmes, setProgrammes] = useState([]);
  const [programmeFilter, setProgrammeFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState(null); // { user_id, name, programme_title }
  const [source, setSource] = useState("file"); // 'file' | 'link'
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = [];
    if (programmeFilter && programmeFilter !== "all") params.push(`programme_id=${encodeURIComponent(programmeFilter)}`);
    if (search.trim()) params.push(`q=${encodeURIComponent(search.trim())}`);
    const url = `/admin/admission-letters/students${params.length ? "?" + params.join("&") : ""}`;
    api.get(url).then(r => { setRows(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [programmeFilter]);
  useEffect(() => { api.get("/public-programmes").then(r => setProgrammes(r.data || [])).catch(() => {}); }, []);

  const openUpload = (row) => {
    setTarget(row);
    setSource("file");
    setFile(null);
    setExternalUrl("");
    setFilename("");
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!target) return;
    if (source === "file" && !file) return toast.error("Pick a file or switch to URL");
    if (source === "link" && !externalUrl.trim()) return toast.error("Paste a URL or switch to file");
    setSaving(true);
    try {
      const fd = new FormData();
      if (source === "file") {
        fd.append("file", file);
      } else {
        fd.append("external_url", externalUrl.trim());
        if (filename.trim()) fd.append("filename", filename.trim());
      }
      await api.post(`/users/${target.user_id}/admission-letter`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Admission letter uploaded — student can now view it.");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setSaving(false);
  };

  const view = (row) => {
    if (row.letter?.kind === "link" && row.letter?.external_url) {
      window.open(row.letter.external_url, "_blank", "noopener");
    } else {
      window.open(`${BACKEND_URL}/api/users/${row.user_id}/admission-letter`, "_blank");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete admission letter for ${row.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${row.user_id}/admission-letter`);
      toast.success("Admission letter removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="admin-admission-letters-page">
        <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Admin · Students</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Admission Letters
        </h1>
        <p className="text-sm text-slate-600 mb-6 max-w-2xl">
          Upload a per-student admission letter (PDF/DOCX) or paste a Google Drive / cloud link.
          Students see and download/open their letter from their dashboard.
        </p>

        <div className="mb-4 flex flex-wrap gap-2 items-end">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") load(); }}
              placeholder="Search by name or email…"
              className="pl-9 rounded-none"
              data-testid="admission-search"
            />
          </div>
          <select
            value={programmeFilter}
            onChange={e => setProgrammeFilter(e.target.value)}
            className="border border-slate-200 px-3 py-2 text-sm rounded-none bg-white"
            data-testid="admission-programme-filter"
          >
            <option value="all">All programmes</option>
            {programmes.map(p => (
              <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.title}</option>
            ))}
          </select>
          <Button onClick={load} variant="outline" className="rounded-none gap-1 text-sm" data-testid="admission-apply-filters">
            Apply
          </Button>
          <span className="text-xs text-slate-500" data-testid="admission-results-count">{rows.length} student{rows.length === 1 ? "" : "s"}</span>
        </div>

        {loading ? (
          <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-16 bg-slate-100 animate-pulse" />)}</div>
        ) : rows.length === 0 ? (
          <Card className="border border-slate-200 rounded-none">
            <CardContent className="p-10 text-center">
              <FileText size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No programme-enrolled students match your filters yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-sm" data-testid="admission-table">
              <thead>
                <tr className="bg-slate-950 text-white">
                  <th className="text-left px-4 py-3 font-medium">Student</th>
                  <th className="text-left px-3 py-3 font-medium">Programme</th>
                  <th className="text-left px-3 py-3 font-medium">Letter</th>
                  <th className="text-left px-3 py-3 font-medium">Enrolment</th>
                  <th className="text-right px-3 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.user_id}-${r.programme_id}`} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`} data-testid={`admission-row-${r.user_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{r.programme_title || "—"}</td>
                    <td className="px-3 py-3">
                      {r.letter ? (
                        <div className="flex items-center gap-2 text-xs">
                          {r.letter.kind === "link" ? <LinkIcon size={12} className="text-blue-700" /> : <FileText size={12} className="text-emerald-700" />}
                          <span className="truncate max-w-[200px] inline-block align-middle" title={r.letter.filename || r.letter.external_url}>{r.letter.filename || "Link"}</span>
                        </div>
                      ) : (
                        <Badge className="rounded-none text-[10px] bg-slate-200 text-slate-600">Not uploaded</Badge>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <Badge className="rounded-none text-[10px] capitalize bg-slate-100 text-slate-700">{(r.enrollment_status || "").replace("_", " ")}</Badge>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {r.letter && (
                        <Button size="sm" variant="outline" onClick={() => view(r)} className="rounded-none gap-1 text-xs mr-1" data-testid={`view-letter-${r.user_id}`}>
                          <Eye size={12} /> View
                        </Button>
                      )}
                      <Button size="sm" onClick={() => openUpload(r)} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-1 text-xs mr-1" data-testid={`upload-letter-${r.user_id}`}>
                        <UploadSimple size={12} weight="bold" /> {r.letter ? "Replace" : "Upload"}
                      </Button>
                      {r.letter && (
                        <Button size="sm" variant="outline" onClick={() => remove(r)} className="rounded-none gap-1 text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-letter-${r.user_id}`}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
                Upload admission letter
              </DialogTitle>
            </DialogHeader>
            {target && (
              <p className="text-xs text-slate-500 -mt-2 mb-2">
                For <b className="text-slate-900">{target.name}</b> · {target.programme_title}
              </p>
            )}
            <div className="space-y-3">
              <div className="flex gap-2">
                {["file", "link"].map(s => (
                  <button
                    key={s}
                    onClick={() => setSource(s)}
                    className={`flex-1 px-3 py-2 text-xs uppercase font-bold tracking-wider border-2 ${source === s ? "border-blue-800 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-500 hover:border-blue-300"}`}
                    data-testid={`source-${s}`}
                  >
                    {s === "file" ? "Upload File" : "Paste URL"}
                  </button>
                ))}
              </div>
              {source === "file" ? (
                <div>
                  <Label className="text-sm">PDF / DOCX / image</Label>
                  <Input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files?.[0])} className="rounded-none mt-1" data-testid="letter-file-input" />
                  <p className="text-[11px] text-slate-500 mt-1">Accepted: PDF, DOC/DOCX, PNG, JPG</p>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-sm">External URL (Google Drive, Dropbox, S3…)</Label>
                    <Input value={externalUrl} onChange={e => setExternalUrl(e.target.value)} placeholder="https://drive.google.com/…" className="rounded-none mt-1" data-testid="letter-url-input" />
                    <p className="text-[11px] text-slate-500 mt-1">Make sure the link is shareable (Anyone with the link can view).</p>
                  </div>
                  <div>
                    <Label className="text-sm">Display label (optional)</Label>
                    <Input value={filename} onChange={e => setFilename(e.target.value)} placeholder="e.g. Admission Letter — Cohort 2026" className="rounded-none mt-1" data-testid="letter-label-input" />
                  </div>
                </>
              )}
              <Button onClick={submit} disabled={saving} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="letter-submit">
                {saving ? "Saving…" : (<><UploadSimple size={14} weight="bold" /> Save admission letter</>)}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
